// OrganizerPingsSection — Feb 2026 restructure. Renamed to "Calendar Pings"
// and now embedded inside the AI Calendar frame as a collapsible sub-button.
// (File name kept for git history + external references.) The DIRECTED
// organizer questions still fire on a real calendar schedule and answer
// against the silo's live Key Data / calendar.
//
// Props:
//   • siloId, inheritedSkus — same as before.
//   • embedded (bool, default false) — when true, renders without the outer
//     <section> wrapper (used inside the AI Calendar frame). When false, keeps
//     the standalone card look (kept for any external callers).
//   • showInfo (bool, default false) — when true, an "eye" info button next
//     to the title opens the "Two kinds of pings" explainer, contextualized
//     to Calendar Pings.
import { useEffect, useState } from "react";
import { BellRinging, Plus, Sparkle, PencilSimple, Trash, Pause, Play, CalendarHeart, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useEntitlements } from "@/lib/entitlements";
import { EventEditorModal, DOW_MIN, isoLocal } from "@/components/CalendarSection";
import PingTypesExplainer from "@/components/PingTypesExplainer";

const fmt12 = (hhmm) => {
  if (!hhmm) return "any time";
  const [h, m] = hhmm.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ap}`;
};

export default function OrganizerPingsSection({ siloId, inheritedSkus = [], embedded = false, showInfo = false, members = [] }) {
  const { owns } = useEntitlements();
  const unlocked = owns("ai_calendar") || inheritedSkus.includes("ai_calendar");
  const [pings, setPings] = useState([]);
  const [editor, setEditor] = useState(null); // null | { event } | { defaults }
  const [infoOpen, setInfoOpen] = useState(false);

  const load = async () => {
    try {
      const today = new Date();
      const end = new Date(today); end.setDate(end.getDate() + 6);
      const r = await api.get(`/calendar/${siloId}/events`, { params: { start: isoLocal(today), end: isoLocal(end) } });
      setPings((r.data.events || []).filter((e) => e.kind === "ping"));
    } catch { /* locked / errored — section stays empty */ }
  };
  useEffect(() => {
    if (!unlocked) return;
    load();
    const h = () => load();
    window.addEventListener("mysilo:calendar-refresh", h);
    return () => window.removeEventListener("mysilo:calendar-refresh", h);
    // eslint-disable-next-line
  }, [siloId, unlocked]);

  if (!unlocked) return null;

  const del = async (p) => {
    if (!window.confirm("Delete this Calendar Ping?")) return;
    try {
      await api.delete(`/calendar/events/${p.id}`);
      toast.success("Ping deleted.");
      load();
      window.dispatchEvent(new Event("mysilo:calendar-refresh"));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't delete."); }
  };
  const togglePause = async (p) => {
    const pausing = p.enabled !== false;
    try {
      await api.patch(`/calendar/events/${p.id}`, { enabled: !pausing });
      toast.success(pausing ? "Ping paused — resume it any time." : "Ping resumed.");
      load();
      window.dispatchEvent(new Event("mysilo:calendar-refresh"));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't do that."); }
  };
  const RECAP_TITLE = "Sunday family recap";
  const hasRecap = pings.some((p) => (p.title || "").toLowerCase() === RECAP_TITLE.toLowerCase());
  const addRecap = async () => {
    const d = new Date();
    d.setDate(d.getDate() + ((7 - d.getDay()) % 7)); // next Sunday (or today)
    try {
      await api.post(`/calendar/${siloId}/events`, {
        kind: "ping", title: RECAP_TITLE,
        ping_question: "Give me our weekly family recap: what did everyone get done last week (chores, routines, events), whose streaks are alive, what got missed, and what's coming up for the whole family this week?",
        start_date: isoLocal(d), start_time: "17:00", all_day: false,
        tz_offset_minutes: new Date().getTimezoneOffset(),
        recurrence: { freq: "weekly", dow: [0], until: null }, reminders: [],
      });
      toast.success("Sunday recap is on — every Sunday at 5 PM.");
      load();
      window.dispatchEvent(new Event("mysilo:calendar-refresh"));
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add."); }
  };
  const schedule = (p) => {
    const rec = p.recurrence || {};
    const days = (rec.dow || []).length ? (rec.dow || []).map((d) => DOW_MIN[d]).join("·")
      : (rec.freq === "weekly" ? "weekly" : "daily");
    return `${days} · ${fmt12(p.start_time)}`;
  };

  // Feb 2026 — new Calendar Pings copy (per user's spec).
  const description = (
    <p className="text-xs text-[#6B6A68] leading-relaxed mb-3 max-w-2xl">
      Directed questions to your Silo Calendar — pushed to your phone at the time
      you pick and answered against your <strong>Key Data</strong>. Pick from the
      staples, let the AI suggest more, or write your own.
      <br />
      <span className="text-[11px] italic">
        (Different from the Question bank above, which throws random questions at random times.)
      </span>
    </p>
  );

  const inner = (
    <>
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <BellRinging size={16} weight="duotone" className="text-[#C16E5A]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">Calendar Pings</h3>
        {showInfo && (
          <button onClick={() => setInfoOpen(true)} data-testid="calendar-pings-info-btn"
            title="Calendar Pings vs Question bank + Scheduled pings — what's the difference?"
            className="p-1 rounded-full text-[#6B6A68] hover:text-[#2C6E92] hover:bg-[#E3EFF7] transition-colors">
            <Info size={15} weight="duotone" />
          </button>
        )}
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">({pings.length})</span>
        <span className="flex-1" />
        {!hasRecap && (
          <button onClick={addRecap} data-testid="organizer-ping-recap"
            title="One tap: a Sunday 5 PM ping that recaps the family's week — chores done, streaks, what's ahead"
            className="text-xs px-3 py-1.5 rounded-md border border-[#B9D4BE] text-[#4A5D4E] bg-[#F4F8F5] hover:bg-[#EAF3EC] flex items-center gap-1.5">
            <CalendarHeart size={12} weight="duotone" /> Sunday recap
          </button>
        )}
        <button onClick={() => setEditor({ defaults: { kind: "ping", openGallery: true } })} data-testid="organizer-ping-browse"
          className="text-xs px-3 py-1.5 rounded-md border border-[#C19A4A] text-[#8A6A24] bg-[#F8F1E2] hover:bg-[#F3E7CB] flex items-center gap-1.5">
          <Sparkle size={12} weight="duotone" /> Browse suggestions
        </button>
        <button onClick={() => setEditor({ defaults: { kind: "ping" } })} data-testid="organizer-ping-new"
          className="text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5">
          <Plus size={12} weight="bold" /> New ping
        </button>
      </div>
      {description}
      {pings.length === 0 ? (
        <div className="text-[11px] text-[#6B6A68] italic" data-testid="organizer-pings-empty">
          No Calendar Pings yet — start with "Browse suggestions" for a list of great staples.
        </div>
      ) : (
        <div className="space-y-1.5">
          {pings.map((p) => {
            const paused = p.enabled === false;
            return (
            <div key={p.id} className={`flex items-center gap-2 text-sm bg-[#FDFCF8] border hairline rounded px-2.5 py-2 ${paused ? "opacity-60" : ""}`} data-testid={`organizer-ping-${p.id}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full shrink-0 ${paused ? "bg-[#B5B3AE]" : "bg-[#C16E5A]"}`} />
              <div className="flex-1 min-w-0">
                <div className={`text-[#1C1B1A] truncate ${paused ? "line-through" : ""}`}>{p.title || p.ping_question}</div>
                {p.ping_question && p.ping_question !== p.title && (
                  <div className="text-[11px] text-[#6B6A68] truncate">{p.ping_question}</div>
                )}
              </div>
              {/* Feb 2026 (Randy) — recipient chip. Makes it obvious who
                  the ping actually pings ("→ Levi" / "everyone"). Falls
                  back to "just me" if nothing routed. */}
              <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded shrink-0 bg-[#E3EFF7] text-[#2C6E92] border border-[#B9CDE0]"
                title="Who gets this ping" data-testid={`organizer-ping-recipient-${p.id}`}>
                → {p.notify_scope === "everyone" ? "everyone"
                   : p.notify_scope === "parents" ? "all parents"
                   : p.assignee_name ? p.assignee_name
                   : "just me"}
              </span>
              {paused ? (
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#6B6A68] bg-[#F0F1EE] rounded px-1.5 py-0.5 shrink-0" data-testid={`organizer-ping-paused-${p.id}`}>paused</span>
              ) : (
                <span className="text-[10px] text-[#2C6E92] bg-[#E3EFF7] rounded px-1.5 py-0.5 shrink-0">{schedule(p)}</span>
              )}
              <button onClick={() => togglePause(p)} title={paused ? "Resume" : "Pause"} data-testid={`organizer-ping-pause-${p.id}`}
                className="p-1.5 rounded text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]">
                {paused ? <Play size={14} weight="duotone" /> : <Pause size={14} weight="duotone" />}
              </button>
              <button onClick={() => setEditor({ event: p })} data-testid={`organizer-ping-edit-${p.id}`}
                className="p-1.5 rounded text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#F0F1EE]"><PencilSimple size={14} /></button>
              <button onClick={() => del(p)} data-testid={`organizer-ping-delete-${p.id}`}
                className="p-1.5 rounded text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#F0F1EE]"><Trash size={14} /></button>
            </div>
            );
          })}
        </div>
      )}

      {editor && (
        <EventEditorModal siloId={siloId} editing={editor.event || null} defaults={editor.defaults || {}}
          members={members}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); load(); window.dispatchEvent(new Event("mysilo:calendar-refresh")); }} />
      )}
      {infoOpen && <PingTypesExplainer onClose={() => setInfoOpen(false)} context="calendar" />}
    </>
  );

  if (embedded) {
    return <div data-testid="staging-organizer-pings">{inner}</div>;
  }
  return (
    <section className="bg-white border hairline rounded-lg p-5" data-testid="staging-organizer-pings">
      {inner}
    </section>
  );
}
